/**
 * Messenger API Service
 *
 * REST API client for messenger operations including:
 * - Conversations management
 * - Message sending and retrieval
 * - File uploads
 * - Reactions
 *
 * Includes a mapping layer to transform snake_case backend responses
 * into camelCase frontend types.
 */

import type {
  Conversation,
  Message,
  MessengerParticipant,
  MessageReaction,
  FileAttachment,
  GetConversationsParams,
  GetMessagesParams,
  SendMessageParams,
  CreateConversationParams,
  UpdateConversationParams,
  AddReactionParams,
} from '../types/messenger';

// ── Backend response types (snake_case from Pydantic) ─────────────────

interface BackendSenderInfo {
  id: string;
  type: string;
  display_name: string;
  avatar_url?: string | null;
  status?: string | null;
}

interface BackendAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  thumbnail_url?: string | null;
  created_at: string;
}

interface BackendMessage {
  id: string;
  conversation_id: string;
  sender_type: string;
  sender_id: string | null;
  content: string | null;
  content_type: string;
  reply_to_id?: string | null;
  thread_id?: string | null;
  is_edited: boolean;
  is_deleted: boolean;
  reactions: Record<string, string[]>;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  sender?: BackendSenderInfo | null;
  attachments: BackendAttachment[];
  thread_count: number;
}

interface BackendMember {
  id: string;
  member_type: string;
  member_id: string;
  role: string;
  joined_at: string;
  last_read_at?: string | null;
  notifications_enabled: boolean;
  is_pinned: boolean;
  display_name?: string | null;
  avatar_url?: string | null;
  status?: string | null;
}

interface BackendConversation {
  id: string;
  type: string;
  name?: string | null;
  description?: string | null;
  icon?: string | null;
  is_private: boolean;
  is_archived: boolean;
  is_muted: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  last_message_at?: string | null;
  members: BackendMember[];
  unread_count: number;
  last_message_preview?: string | null;
}

// ── Mapping functions (backend → frontend) ────────────────────────────

export function mapMessage(backend: BackendMessage): Message {
  return {
    id: backend.id,
    conversationId: backend.conversation_id,
    senderId: backend.sender?.id || backend.sender_id || '',
    senderName: backend.sender?.display_name || 'Unknown',
    senderAvatarUrl: backend.sender?.avatar_url || undefined,
    content: backend.content || '',
    contentType: (backend.content_type || 'text') as Message['contentType'],
    replyToId: backend.reply_to_id || undefined,
    threadId: backend.thread_id || undefined,
    threadCount: backend.thread_count || 0,
    isEdited: backend.is_edited,
    isPinned: false,
    createdAt: backend.created_at,
    reactions: mapReactions(backend.reactions),
    attachments: (backend.attachments || []).map(mapAttachment),
  };
}

function mapAttachment(backend: BackendAttachment): FileAttachment {
  return {
    id: backend.id,
    filename: backend.file_name,
    mimeType: backend.file_type,
    size: backend.file_size,
    url: backend.file_url,
    thumbnailUrl: backend.thumbnail_url || undefined,
    createdAt: backend.created_at,
  };
}

function mapReactions(reactions: Record<string, string[]>): MessageReaction[] {
  const result: MessageReaction[] = [];
  if (!reactions) return result;
  for (const [emoji, userIds] of Object.entries(reactions)) {
    for (const userId of userIds) {
      result.push({ emoji, userId, userName: '', createdAt: '' });
    }
  }
  return result;
}

export function mapConversation(backend: BackendConversation): Conversation {
  const participants: MessengerParticipant[] = (backend.members || []).map((m) => ({
    id: m.member_id,
    name: m.display_name || 'Unknown',
    avatarUrl: m.avatar_url || undefined,
    status: (m.status as MessengerParticipant['status']) || undefined,
  }));

  return {
    id: backend.id,
    type: backend.type as Conversation['type'],
    name: backend.name || undefined,
    description: backend.description || undefined,
    avatarUrl: backend.icon || undefined,
    participants,
    lastMessage: backend.last_message_preview
      ? ({
          id: '',
          conversationId: backend.id,
          senderId: '',
          senderName: '',
          content: backend.last_message_preview,
          contentType: 'text',
          createdAt: backend.last_message_at || backend.updated_at,
        } as Message)
      : undefined,
    unreadCount: backend.unread_count || 0,
    isMuted: backend.is_muted || false,
    isPinned: false,
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
  };
}

// ── API Service ───────────────────────────────────────────────────────

class MessengerAPIService {
  private baseUrl = '';
  private token = '';

  configure(baseUrl: string, token: string) {
    this.baseUrl = baseUrl
      .replace(/\/$/, '')
      .replace(/\/ws$/, '')
      .replace(/\/api\/v1$/, '')
      .replace(/\/$/, '');
    this.token = token;
  }

  setToken(token: string) {
    this.token = token;
  }

  private async request<T>(
    method: string,
    endpoint: string,
    body?: unknown,
    isFormData = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.token}`,
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      method,
      headers,
      body: isFormData ? (body as FormData) : body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || error.detail || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  // ── Conversations ─────────────────────────────────────────────────

  async getConversations(params: GetConversationsParams = {}): Promise<{
    conversations: Conversation[];
    total: number;
    hasMore: boolean;
  }> {
    const searchParams = new URLSearchParams();
    const perPage = params.limit || 50;
    const page = params.offset ? Math.floor(params.offset / perPage) + 1 : 1;
    searchParams.set('page', String(page));
    searchParams.set('per_page', String(perPage));
    if (params.type) searchParams.set('type', params.type);

    const query = searchParams.toString();
    const response = await this.request<{
      items: BackendConversation[];
      total: number;
      has_more: boolean;
    }>('GET', `/api/v1/conversations${query ? `?${query}` : ''}`);

    return {
      conversations: (response.items || []).map(mapConversation),
      total: response.total,
      hasMore: response.has_more,
    };
  }

  async getConversation(conversationId: string): Promise<Conversation> {
    const response = await this.request<BackendConversation>(
      'GET',
      `/api/v1/conversations/${conversationId}`
    );
    return mapConversation(response);
  }

  async createConversation(params: CreateConversationParams): Promise<Conversation> {
    const response = await this.request<BackendConversation>('POST', '/api/v1/conversations', {
      type: params.type,
      name: params.name,
      description: params.description,
      member_ids: params.participantIds,
    });
    return mapConversation(response);
  }

  async updateConversation(params: UpdateConversationParams): Promise<Conversation> {
    const { conversationId, ...data } = params;
    const response = await this.request<BackendConversation>(
      'PATCH',
      `/api/v1/conversations/${conversationId}`,
      data
    );
    return mapConversation(response);
  }

  // ── Messages ──────────────────────────────────────────────────────

  async getMessages(params: GetMessagesParams): Promise<{
    messages: Message[];
    hasMore: boolean;
  }> {
    const { conversationId, ...queryParams } = params;
    const searchParams = new URLSearchParams();
    if (queryParams.limit) searchParams.set('limit', String(queryParams.limit));
    if (queryParams.before) searchParams.set('before', queryParams.before);
    if (queryParams.after) searchParams.set('after', queryParams.after);

    const query = searchParams.toString();
    const response = await this.request<{
      items: BackendMessage[];
      total: number;
      has_more: boolean;
    }>(
      'GET',
      `/api/v1/messages/conversation/${conversationId}${query ? `?${query}` : ''}`
    );

    return {
      messages: (response.items || []).map(mapMessage),
      hasMore: response.has_more,
    };
  }

  async sendMessage(params: SendMessageParams): Promise<Message> {
    const { conversationId, attachments, ...messageData } = params;

    const backendData = {
      content: messageData.content,
      content_type: messageData.contentType || 'text',
      reply_to_id: messageData.replyToId || undefined,
      thread_id: messageData.threadId || undefined,
    };

    if (attachments && attachments.length > 0) {
      const formData = new FormData();
      formData.append('content', backendData.content);
      if (backendData.content_type) formData.append('content_type', backendData.content_type);
      if (backendData.reply_to_id) formData.append('reply_to_id', backendData.reply_to_id);
      if (backendData.thread_id) formData.append('thread_id', backendData.thread_id);
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });

      const response = await this.request<BackendMessage>(
        'POST',
        `/api/v1/messages/conversation/${conversationId}`,
        formData,
        true
      );
      return mapMessage(response);
    }

    const response = await this.request<BackendMessage>(
      'POST',
      `/api/v1/messages/conversation/${conversationId}`,
      backendData
    );
    return mapMessage(response);
  }

  async editMessage(_conversationId: string, messageId: string, content: string): Promise<Message> {
    const response = await this.request<BackendMessage>(
      'PATCH',
      `/api/v1/messages/${messageId}`,
      { content }
    );
    return mapMessage(response);
  }

  async deleteMessage(_conversationId: string, messageId: string): Promise<void> {
    return this.request('DELETE', `/api/v1/messages/${messageId}`);
  }

  // ── Read status ───────────────────────────────────────────────────

  async markAsRead(conversationId: string, messageId?: string): Promise<void> {
    return this.request('PUT', `/api/v1/conversations/${conversationId}/read`, {
      messageId,
    });
  }

  async getUnreadCount(): Promise<{ total: number; conversations: Record<string, number> }> {
    return this.request('GET', '/api/v1/conversations/unread');
  }

  // ── Reactions ─────────────────────────────────────────────────────

  async addReaction(params: AddReactionParams): Promise<void> {
    const { messageId, emoji } = params;
    return this.request('POST', `/api/v1/messages/${messageId}/reactions`, { emoji });
  }

  async removeReaction(_conversationId: string, messageId: string, emoji: string): Promise<void> {
    return this.request(
      'DELETE',
      `/api/v1/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
    );
  }

  // ── Pins ──────────────────────────────────────────────────────────

  async pinMessage(_conversationId: string, messageId: string): Promise<void> {
    return this.request('POST', `/api/v1/messages/${messageId}/pin`);
  }

  async unpinMessage(_conversationId: string, messageId: string): Promise<void> {
    return this.request('DELETE', `/api/v1/messages/${messageId}/pin`);
  }

  async getPinnedMessages(conversationId: string): Promise<Message[]> {
    const response = await this.request<BackendMessage[]>(
      'GET',
      `/api/v1/messages/conversation/${conversationId}/pinned`
    );
    return response.map(mapMessage);
  }

  // ── Search ────────────────────────────────────────────────────────

  async searchMessages(
    query: string,
    conversationId?: string
  ): Promise<{ messages: Message[]; total: number }> {
    const searchParams = new URLSearchParams({ q: query });
    if (conversationId) searchParams.set('conversationId', conversationId);
    const response = await this.request<{
      items: BackendMessage[];
      total: number;
    }>('GET', `/api/v1/messages/search?${searchParams.toString()}`);
    return {
      messages: (response.items || []).map(mapMessage),
      total: response.total,
    };
  }

  // ── Files ─────────────────────────────────────────────────────────

  async uploadFile(
    conversationId: string,
    file: File
  ): Promise<{ uploadUrl: string; fileId: string }> {
    return this.request('POST', `/api/v1/conversations/${conversationId}/upload`, {
      filename: file.name,
      mimeType: file.type,
      size: file.size,
    });
  }

  // ── Direct Messages ───────────────────────────────────────────────

  async getOrCreateDM(targetId: string, targetType: string = 'user'): Promise<Conversation> {
    const response = await this.request<BackendConversation>('POST', '/api/v1/conversations/dm', {
      target_type: targetType,
      target_id: targetId,
    });
    return mapConversation(response);
  }
}

// Export singleton instance
export const messengerAPI = new MessengerAPIService();

export default messengerAPI;
